import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useContentStableValue } from '@/shared/hooks/useContentStableValue';

import { useRecaptchaScript } from '../../../hooks/useRecaptchaScript';
import { getRecaptchaClient } from '../../../lib/recaptcha';
import type {
  ConfigurableFormWidgetProps,
  FormSubmitValues,
  TechnicalStatus,
} from './form-widget.types';
import {
  createSummaryRows,
  getFirstName,
  getLoggedInMessage,
  hasFilledValue,
} from './form-display';
import { createDefaultValues, createSubmitPayload } from './form-payload';
import { createFormSchema } from './form-schema';
import { isFieldVisible } from './form-visibility';
import { FormSubmitError, submitForm } from './mock-submit';

type UseConfigurableFormOptions = Pick<
  ConfigurableFormWidgetProps,
  | 'config'
  | 'mockResult'
  | 'onError'
  | 'onSubmitPayload'
  | 'onSuccess'
  | 'onValidationError'
  | 'pageUrl'
  | 'prefillValues'
  | 'editablePrefill'
  | 'extraFields'
  | 'selectedValues'
  | 'recaptchaSiteKey'
  | 'requireSubmitUrl'
  | 'scenario'
  | 'source'
  | 'submitUrl'
  | 'successRedirectUrl'
  | 'trackingId'
>;

export function useConfigurableForm({
  config,
  mockResult = 'success',
  onError,
  onSubmitPayload,
  onSuccess,
  onValidationError,
  pageUrl,
  prefillValues,
  editablePrefill = false,
  extraFields,
  selectedValues,
  recaptchaSiteKey,
  requireSubmitUrl = false,
  scenario,
  source,
  submitUrl,
  successRedirectUrl,
  trackingId,
}: UseConfigurableFormOptions) {
  const [status, setStatus] = useState<TechnicalStatus>('idle');
  const [serverErrorMessage, setServerErrorMessage] = useState<string | undefined>(undefined);
  const isSubmittingRef = useRef(false);
  // Readonly fields (e.g. a server-provided program name) prefilled from the host
  // are NOT account data, so they must not trigger the logged-in account UI.
  const readonlyFieldNames = useMemo(
    () => new Set(config.fields.filter((f) => f.type === 'readonly').map((f) => f.name)),
    [config.fields],
  );
  // Datos reales (prefillValues) tienen prioridad; `scenario` solo carga datos de ejemplo en preview/doc.
  const stablePrefillValues = useContentStableValue(prefillValues);
  const resolvedPrefill = useMemo<FormSubmitValues | undefined>(() => {
    if (stablePrefillValues) return stablePrefillValues;
    if (scenario && scenario !== 'empty') return config.scenarioValues?.[scenario];
    return undefined;
  }, [config, stablePrefillValues, scenario]);
  const meaningfulPrefillKeys =
    resolvedPrefill != null
      ? Object.keys(resolvedPrefill).filter((key) => !readonlyFieldNames.has(key))
      : [];
  const isLoggedInScenario =
    !editablePrefill &&
    (meaningfulPrefillKeys.length > 0 ||
      (scenario !== undefined && scenario !== 'empty' && prefillValues == null));
  const relaxedIdentificationFieldNames = useMemo(() => {
    if (!isLoggedInScenario || !resolvedPrefill?.identificationNumber) return undefined;

    return new Set(
      config.fields
        .filter(
          (field) =>
            field.validation?.type === 'identification' &&
            resolvedPrefill[field.name] !== undefined &&
            resolvedPrefill[field.name] !== '',
        )
        .map((field) => field.name),
    );
  }, [config.fields, isLoggedInScenario, resolvedPrefill]);
  const schema = useMemo(
    () => createFormSchema(config, { relaxedIdentificationFieldNames }),
    [config, relaxedIdentificationFieldNames],
  );
  const defaultValues = useMemo(
    () => createDefaultValues(config, resolvedPrefill),
    [config, resolvedPrefill],
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormSubmitValues>({
    resolver: zodResolver(schema),
    // Por pasos el error aparece al validar el paso; `onTouched` lo limpia
    // mientras el usuario corrige, sin esperar a que salga del campo.
    mode: config.steps?.length ? 'onTouched' : 'onBlur',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const watchedValues = useWatch({ control });
  const effectiveValues = useMemo<FormSubmitValues>(() => {
    const values: FormSubmitValues = { ...defaultValues };

    Object.entries(watchedValues).forEach(([name, value]) => {
      if (value !== undefined) values[name] = value;
    });

    return values;
  }, [defaultValues, watchedValues]);

  const dependentsByController = useMemo(() => {
    const map = new Map<string, string[]>();
    config.fields.forEach((field) => {
      const controllers = [
        ...(field.optionsWhen?.fields ??
          (field.optionsWhen?.field ? [field.optionsWhen.field] : [])),
        ...(field.visibleWhen ? [field.visibleWhen.field] : []),
      ];
      controllers.forEach((controller) => {
        map.set(controller, [...(map.get(controller) ?? []), field.name]);
      });
    });
    return map;
  }, [config.fields]);

  const prevControllerValues = useRef<Record<string, unknown>>({});
  useEffect(() => {
    dependentsByController.forEach((dependents, controller) => {
      const current = effectiveValues[controller];
      const previous = prevControllerValues.current[controller];
      if (previous !== undefined && previous !== current) {
        dependents.forEach((name) => {
          if (effectiveValues[name] !== '' && effectiveValues[name] !== undefined) {
            setValue(name, '');
          }
        });
      }
      prevControllerValues.current[controller] = current;
    });
  }, [dependentsByController, effectiveValues, setValue]);

  const visibleFields = useMemo(
    () => config.fields.filter((field) => isFieldVisible(field, effectiveValues)),
    [config.fields, effectiveValues],
  );
  const renderableFields = useMemo(
    () => visibleFields.filter((field) => !field.hidden),
    [visibleFields],
  );
  // Solo lo que trajo el prefill es "dato de tu cuenta". Armar el resumen con
  // todos los valores por defecto convertía en dato de cuenta cualquier campo
  // con default de config —`identificationCountry` arranca en el país del
  // sitio—, y eso lo dejaba de solo lectura: quien no es de ese país no podía
  // corregirlo ni elegir "Otro".
  const accountFields = useMemo(
    () =>
      resolvedPrefill == null
        ? []
        : renderableFields.filter((field) => hasFilledValue(resolvedPrefill[field.name])),
    [renderableFields, resolvedPrefill],
  );
  const accountSummaryRows = useMemo(
    () => (isLoggedInScenario ? createSummaryRows(accountFields, defaultValues) : []),
    [accountFields, defaultValues, isLoggedInScenario],
  );
  const accountFieldNames = useMemo(
    () => Array.from(new Set(accountSummaryRows.flatMap((row) => row.fieldNames))),
    [accountSummaryRows],
  );
  const missingRequiredFields = useMemo(
    () =>
      visibleFields.filter(
        (field) =>
          field.required &&
          field.type !== 'readonly' &&
          field.type !== 'checkbox' &&
          !accountFieldNames.includes(field.name),
      ),
    [accountFieldNames, visibleFields],
  );
  const hasEditableFields = isLoggedInScenario && missingRequiredFields.length > 0;
  const isLoggedInComplete = isLoggedInScenario && !hasEditableFields;
  const [stepIndex, setStepIndex] = useState(0);
  // Un paso solo agrupa campos ya renderizables: si uno se oculta por
  // `visibleWhen`/`optionsWhen`, desaparece del paso sin romper la navegación.
  const steps = useMemo(() => {
    if (!config.steps?.length || isLoggedInComplete) return undefined;

    return config.steps.map((step) => ({
      ...step,
      fields: renderableFields.filter((field) => step.fields.includes(field.name)),
    }));
  }, [config.steps, isLoggedInComplete, renderableFields]);
  const currentStepIndex = steps ? Math.min(stepIndex, steps.length - 1) : 0;
  const currentStep = steps?.[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = !steps || currentStepIndex === steps.length - 1;
  const formFields = isLoggedInComplete ? [] : (currentStep?.fields ?? renderableFields);

  // El paso está completo cuando ningún error del schema cae en sus campos:
  // así "Continuar" solo se habilita con el paso resuelto.
  const isCurrentStepValid = useMemo(() => {
    if (!currentStep) return true;

    const result = schema.safeParse(effectiveValues);
    if (result.success) return true;

    const stepFieldNames = new Set(currentStep.fields.map((field) => field.name));
    return !result.error.issues.some((issue) => stepFieldNames.has(String(issue.path[0])));
  }, [currentStep, effectiveValues, schema]);

  const goToPreviousStep = useCallback(() => {
    setStepIndex((index) => Math.max(0, index - 1));
  }, []);

  // Solo valida los campos del paso actual: el empleador no ve errores de
  // campos que todavía no vio.
  const goToNextStep = useCallback(async () => {
    if (!steps || !currentStep) return;

    const isStepValid = await trigger(currentStep.fields.map((field) => field.name));
    if (!isStepValid) return;

    setStepIndex(Math.min(steps.length - 1, currentStepIndex + 1));
  }, [currentStep, currentStepIndex, steps, trigger]);
  const widgetFieldNames = useMemo(
    () => (isLoggedInScenario ? missingRequiredFields.map((field) => field.name) : undefined),
    [isLoggedInScenario, missingRequiredFields],
  );
  const isSubmittable = useMemo(() => {
    if (isLoggedInComplete) {
      // All required fields are covered by prefill — the server will validate them.
      // Only gate on what the user still needs to interact with: the privacy checkbox.
      if (!config.privacy.enabled || !config.privacy.required) return true;
      return effectiveValues.privacyAccepted === true;
    }
    return schema.safeParse(effectiveValues).success;
  }, [
    config.privacy.enabled,
    config.privacy.required,
    effectiveValues,
    isLoggedInComplete,
    schema,
  ]);
  const firstName = getFirstName(effectiveValues);
  const loggedInMessage = isLoggedInScenario
    ? getLoggedInMessage(config, effectiveValues)
    : undefined;
  const shouldShowRequiredFieldsLabel = !isLoggedInScenario || hasEditableFields;

  useRecaptchaScript(recaptchaSiteKey);

  const getRecaptchaToken = async (): Promise<string | undefined> => {
    if (!recaptchaSiteKey) return undefined;
    const grecaptcha = await getRecaptchaClient(recaptchaSiteKey);
    if (!grecaptcha) return undefined;
    const action = config.variant.replace(/[^A-Za-z0-9_/]/g, '_');
    return new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(recaptchaSiteKey, { action })
          .then(resolve)
          .catch(() => resolve(undefined));
      });
    });
  };

  useEffect(() => {
    reset(defaultValues);
    setStatus('idle');
    setStepIndex(0);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!selectedValues) return;
    const fieldNames = new Set(config.fields.map((field) => field.name));
    Object.entries(selectedValues).forEach(([name, value]) => {
      if (!fieldNames.has(name)) return;
      setValue(name, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    });
  }, [config.fields, defaultValues, selectedValues, setValue]);

  useEffect(() => {
    config.fields.forEach((field) => {
      if (isFieldVisible(field, effectiveValues)) return;
      const currentValue = effectiveValues[field.name];
      const emptyValue = field.type === 'checkbox' ? false : '';
      if (currentValue === emptyValue) return;

      setValue(field.name, emptyValue, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: true,
      });
    });
  }, [config.fields, effectiveValues, setValue]);

  const resetFormState = () => {
    reset(defaultValues);
    setStatus('idle');
    setStepIndex(0);
  };

  const onValidSubmit = async (values: FormSubmitValues) => {
    if (requireSubmitUrl && !submitUrl) {
      setStatus('error');
      onError?.(new Error('Missing required submit-url attribute for adipa-form-widget.'));
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setStatus('submitting');
    setServerErrorMessage(undefined);

    try {
      const recaptchaToken = await getRecaptchaToken();

      const payload = createSubmitPayload(config, values, {
        source,
        trackingId,
        pageUrl,
        accountFieldNames: isLoggedInScenario ? accountFieldNames : undefined,
        widgetFieldNames,
        extraFields,
        recaptchaToken,
      });
      onSubmitPayload?.(payload);

      const response = await submitForm(payload, { submitUrl, result: mockResult });
      setStatus('success');
      onSuccess?.({ payload, response });
      if (successRedirectUrl) {
        window.setTimeout(() => {
          window.location.href = successRedirectUrl;
        }, 1500);
      }
    } catch (err) {
      // Surface the backend-provided message (e.g. "Ya te encuentras registrado")
      // instead of the generic config error when the server returned one.
      if (err instanceof FormSubmitError) {
        const serverMessage =
          err.response?.errors && Object.values(err.response.errors)[0]
            ? Object.values(err.response.errors)[0]
            : err.response?.message;
        setServerErrorMessage(serverMessage || undefined);
      }
      setStatus('error');
      onError?.(err);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const onInvalidSubmit = (error: unknown) => {
    setStatus('idle');
    onValidationError?.(error);
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // En un formulario por pasos el submit avanza mientras queden pasos, así
    // Enter hace lo mismo que el botón y nunca envía a medio llenar.
    if (!isLastStep) {
      void goToNextStep();
      return;
    }

    setStatus('validating');
    if (isLoggedInComplete) {
      // In complete mode, all required fields come from trusted prefill — react-hook-form
      // only has the privacy checkbox registered, so handleSubmit's zodResolver would fail.
      // Skip it and submit with effectiveValues directly.
      void onValidSubmit(effectiveValues);
    } else {
      void handleSubmit(onValidSubmit, onInvalidSubmit)(event);
    }
  };

  return {
    accountFieldNames,
    accountSummaryRows,
    control,
    currentStep,
    currentStepIndex,
    effectiveValues,
    errors,
    firstName,
    formFields,
    goToNextStep,
    goToPreviousStep,
    handleFormSubmit,
    isCurrentStepValid,
    isFirstStep,
    isLastStep,
    hasEditableFields,
    isLoggedInComplete,
    isLoggedInScenario,
    isSubmittable,
    loggedInMessage,
    register,
    resetFormState,
    serverErrorMessage,
    shouldShowRequiredFieldsLabel,
    status,
    steps,
  };
}
